import { useState, useRef, useEffect } from 'react';
import { Linkedin, Instagram, ArrowRight, Phone } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useI18n } from '../lib/i18n';
import { trackEvent } from '../lib/analytics';
import { cn } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Animated container with blur-in effect ──────────────────
function AnimatedContainer({ delay = 0.1, children, className, ...props }) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className} {...props}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ filter: 'blur(4px)', translateY: -8, opacity: 0 }}
      whileInView={{ filter: 'blur(0px)', translateY: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.8 }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ── Footer link groups ──────────────────────────────────────
const footerLinkGroups = [
  {
    label: { pt: 'Navegação', en: 'Navigation' },
    links: [
      { title: { pt: 'Home', en: 'Home' }, modal: null, href: '/' },
      { title: { pt: 'O Instituto', en: 'The Institute' }, modal: 'instituto' },
      { title: { pt: 'Em Foco', en: 'News' }, modal: 'emfoco' },
      { title: { pt: 'Artigos', en: 'Articles' }, modal: 'artigos' },
      { title: 'DEI&A', modal: 'deia' },
      { title: { pt: 'Contato', en: 'Contact' }, modal: 'contato' },
    ],
  },
  {
    label: { pt: 'Atuação', en: 'Programs' },
    links: [
      { title: 'A360', modal: 'projeto-a360' },
      { title: 'HUB Sampa Games', modal: 'projeto-hub-sampa-games' },
      { title: 'AmplificaCine', modal: 'projeto-amplificacine' },
      { title: 'Sororidade Conecta', modal: 'projeto-sororidade-conecta' },
      { title: 'Estúdio', modal: 'projeto-estudio' },
    ],
  },
  {
    label: { pt: 'Recursos', en: 'Resources' },
    links: [
      { title: { pt: 'Soluções', en: 'Solutions' }, modal: 'solucoes' },
      { title: { pt: 'Acessibilidade', en: 'Accessibility' }, modal: 'acessibilidade' },
      { title: { pt: 'Área do Aluno', en: 'Student Area' }, href: '/area-aluno' },
    ],
  },
];

const socialLinks = [
  {
    title: 'LinkedIn',
    href: 'https://www.linkedin.com/company/instituto-br-cultural/',
    icon: Linkedin,
  },
  {
    title: 'Instagram',
    href: 'https://www.instagram.com/institutobrcultural/',
    icon: Instagram,
  },
];

// ── Google Maps embed (Rua Teodoro Sampaio 1109, Pinheiros) ─
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3657.0975!2d-46.6878!3d-23.5614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce579a51f5e1a7%3A0x4e0b2e01f9a2b3c5!2sR.%20Teodoro%20Sampaio%2C%201109%20-%20Pinheiros%2C%20S%C3%A3o%20Paulo%20-%20SP!5e0!3m2!1spt-BR!2sbr!4v1700000000000!5m2!1spt-BR!2sbr';

// ── Sticky section height ───────────────────────────────────
const STICKY_HEIGHT = 520;

const Footer = () => {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/newsletter`, { email });
      if (response.data.success) {
        toast.success(response.data.message);
        trackEvent('newsletter_subscribe', { category: 'conversion' });
        setEmail('');
      }
    } catch {
      toast.error(lang === 'pt' ? 'Erro ao cadastrar email. Tente novamente.' : 'Error subscribing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLinkClick = (link) => {
    if (link.modal) {
      window.dispatchEvent(new CustomEvent('open-modal', { detail: link.modal }));
    }
  };

  return (
    <footer className="w-full">

      {/* ═══════════════════════════════════════════════════════ */}
      {/* NEWSLETTER — normal flow, never clipped                */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="bg-[#FFC107] py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <AnimatedContainer delay={0.05}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 md:gap-16">
              {/* Left — Copy */}
              <div className="md:max-w-[55%]">
                <span className="font-manrope text-xs uppercase tracking-[0.3em] text-black/50 mb-4 block">
                  Newsletter
                </span>
                <h3
                  className="font-oswald text-4xl md:text-6xl uppercase text-black leading-[1.05] mb-5"
                  data-testid="footer-newsletter-title"
                >
                  {t('newsletter.title', 'Fique por dentro do que a gente move')}
                </h3>
                <p className="font-manrope text-black/60 text-base md:text-lg leading-relaxed" style={{ textAlign: 'justify' }}>
                  {t('newsletter.desc', 'Assine a newsletter e receba novidades sobre programas, editais e oportunidades da economia criativa.')}
                </p>
              </div>

              {/* Right — Form */}
              <div className="md:max-w-[40%] w-full">
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-0">
                  <label htmlFor="newsletter-email" className="sr-only">E-mail para newsletter</label>
                  <input
                    id="newsletter-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('newsletter.placeholder', 'Seu melhor e-mail')}
                    autoComplete="email"
                    className="w-full px-0 py-5 outline-none placeholder:text-black/30 text-black font-manrope text-lg bg-transparent border-b-2 border-black/20 focus:border-black transition-colors"
                    data-testid="newsletter-email-input"
                    required
                  />
                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="mt-6 h-14 w-full bg-black text-white uppercase tracking-[0.2em] text-sm font-bold hover:bg-black/85 transition-all disabled:opacity-50 flex items-center justify-center gap-3"
                    data-testid="newsletter-submit-button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading ? 'ENVIANDO...' : t('newsletter.cta', 'QUERO RECEBER')}
                    <ArrowRight size={16} strokeWidth={2.5} />
                  </motion.button>
                  <p className="font-manrope text-[11px] text-black/35 mt-3 text-center">
                    {t('newsletter.disclaimer', 'Uma vez por mês. Sem spam. Cancele quando quiser.')}
                  </p>
                </form>
              </div>
            </div>
          </AnimatedContainer>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* LINKS + MAP — sticky reveal effect                     */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div
        className="relative"
        style={{
          height: `${STICKY_HEIGHT}px`,
          clipPath: 'polygon(0% 0, 100% 0%, 100% 100%, 0 100%)',
        }}
      >
        <div className="fixed bottom-0 w-full" style={{ height: `${STICKY_HEIGHT}px` }}>
          <div
            className="h-full"
            style={{ position: 'sticky', top: `calc(100vh - ${STICKY_HEIGHT}px)` }}
          >
            <div className="relative flex flex-col justify-between h-full py-12 md:py-16 overflow-hidden">

              {/* ── Google Maps background ── */}
              <div className="absolute inset-0 z-0 pointer-events-none" aria-hidden>
                <iframe
                  src={MAPS_EMBED_URL}
                  title="Localização IBRC"
                  className="absolute inset-0 w-full h-full border-0"
                  style={{ filter: 'grayscale(100%) contrast(1.1) brightness(0.35)', opacity: 0.6 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  tabIndex={-1}
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(to bottom, rgb(0,0,0) 0%, rgb(0,0,0) 40%, rgba(0,0,0,0.85) 55%, rgba(0,0,0,0.5) 75%, rgba(0,0,0,0.3) 100%)',
                  }}
                />
              </div>

              {/* ── Content on top of map ── */}
              <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full">
                <div className="flex flex-col md:flex-row gap-10 md:gap-8">

                  {/* Brand block */}
                  <AnimatedContainer className="w-full md:max-w-[280px] min-w-[200px] space-y-5" delay={0.05}>
                    <div className="flex items-center gap-3">
                      <span className="w-3 h-3 bg-[#FFC107] inline-block" />
                      <span className="font-oswald text-xl uppercase text-white tracking-wider">IBRC</span>
                    </div>
                    <p className="font-manrope text-white/50 text-sm leading-relaxed">
                      {t('footer.desc', 'Instituto BR Cultural — cultura, tecnologia e educação transformando a economia criativa do Brasil.')}
                    </p>
                    <div className="flex gap-2">
                      {socialLinks.map((link) => (
                        <motion.a
                          key={link.title}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`${link.title} do IBRC`}
                          className={cn(
                            'w-9 h-9 flex items-center justify-center',
                            'border border-white/15 text-white/50',
                            'hover:border-[#FFC107]/40 hover:text-[#FFC107] transition-all'
                          )}
                          whileHover={{ scale: 1.1, y: -2 }}
                          whileTap={{ scale: 0.9 }}
                          data-testid={`footer-social-${link.title.toLowerCase()}`}
                        >
                          <link.icon size={16} aria-hidden="true" />
                        </motion.a>
                      ))}
                    </div>
                  </AnimatedContainer>

                  {/* Link groups */}
                  {footerLinkGroups.map((group, index) => (
                    <AnimatedContainer
                      key={group.label}
                      delay={0.1 + index * 0.08}
                      className="w-full"
                    >
                      <div className="mb-8 md:mb-0">
                        <h4 className="font-oswald text-sm uppercase tracking-[0.15em] text-white mb-5">
                          {typeof group.label === 'object' ? group.label[lang] : group.label}
                        </h4>
                        <ul className="space-y-2.5 list-none p-0">
                          {group.links.map((link) => {
                            const label = typeof link.title === 'object' ? link.title[lang] : link.title;
                            return (
                            <li key={label}>
                              {link.href ? (
                                <a
                                  href={link.href}
                                  className="font-manrope text-white/50 text-sm hover:text-[#FFC107] transition-colors duration-300 inline-flex items-center"
                                >
                                  {label}
                                </a>
                              ) : (
                                <button
                                  onClick={() => handleLinkClick(link)}
                                  className="font-manrope text-white/50 text-sm hover:text-[#FFC107] transition-colors duration-300 inline-flex items-center bg-transparent border-none cursor-pointer p-0"
                                >
                                  {label}
                                </button>
                              )}
                            </li>
                          );
                          })}
                        </ul>
                      </div>
                    </AnimatedContainer>
                  ))}

                  {/* Contact block */}
                  <AnimatedContainer delay={0.35} className="w-full md:max-w-[240px]">
                    <h4 className="font-oswald text-sm uppercase tracking-[0.15em] text-white mb-5">
                      {t('footer.contato', 'Contato')}
                    </h4>
                    <a
                      href="mailto:contato@institutobrcultural.org"
                      className="block font-manrope text-white/50 text-sm hover:text-[#FFC107] transition-colors mb-3"
                      data-testid="footer-email"
                    >
                      contato@institutobrcultural.org
                    </a>
                    <p className="font-manrope text-white/40 text-sm leading-relaxed mb-4" data-testid="footer-address">
                      Rua Teodoro Sampaio, 1109<br />
                      Pinheiros, São Paulo — SP
                    </p>
                    <motion.a
                      href="https://wa.me/5511925803469?text=Ol%C3%A1%2C%20vim%20pelo%20site%20do%20IBRC!"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackEvent('footer_whatsapp_click', { category: 'engagement' })}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white text-sm font-manrope hover:bg-[#1da851] transition-colors rounded-full mb-4"
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      data-testid="footer-whatsapp"
                    >
                      <Phone size={14} />
                      WhatsApp
                    </motion.a>
                    <br />
                    <button
                      onClick={() => window.dispatchEvent(new CustomEvent('open-modal', { detail: 'acessibilidade' }))}
                      className="font-manrope text-white/40 text-sm hover:text-[#FFC107] transition-colors duration-300 bg-transparent border-none cursor-pointer p-0"
                    >
                      {t('footer.a11y', 'Acessibilidade')}
                    </button>
                  </AnimatedContainer>
                </div>
              </div>

              {/* ── Bottom bar ── */}
              <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full mt-auto pt-6">
                <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <p className="font-manrope text-white/30 text-sm" data-testid="footer-copyright">
                      {t('footer.copyright', '© 2026 Instituto BR Cultural. Todos os direitos reservados.')}
                    </p>
                    <span className="w-1 h-1 bg-white/15 rounded-full hidden md:inline-block" />
                    <p className="font-manrope text-white/20 text-xs">
                      CNPJ 08.541.992/0001-76
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-[#FFC107] inline-block" />
                    <span className="font-manrope text-white/30 text-xs uppercase tracking-widest">
                      {t('footer.tagline', 'Cultura + Tecnologia + Educação')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
